/** Cases — every investigation, filterable, click to open. */
import { useState, useEffect, useCallback } from 'react';
import { api, Spinner, Badge, Hint, EmptyState, STAGE_LABEL, STAGE_TONE, CHANNEL_LABEL, CATEGORY_LABEL, PRIORITY_TONE, money, dateShort } from './ui';

const STAGE_FILTERS = ['', 'intake', 'investigation', 'pending_records', 'clinical_review', 'pending_recovery', 'referred', 'closed'];

export default function CaseList({ onOpenCase }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (stage) params.set('stage', stage);
    if (q) params.set('q', q);
    api(`/cases${params.toString() ? '?' + params.toString() : ''}`)
      .then((j) => setCases(j.cases || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [stage, q]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="font-semibold text-slate-800 flex items-center">All cases<Hint text="Every investigation Sentinel is tracking. Filter by stage or search by case number / description." /></h2>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
          className="ml-auto px-3 py-1.5 rounded-xl border border-slate-200 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-red-200" />
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="px-3 py-1.5 rounded-xl border border-slate-200 text-sm">
          {STAGE_FILTERS.map((s) => <option key={s} value={s}>{s ? STAGE_LABEL[s] : 'All stages'}</option>)}
        </select>
      </div>

      {loading ? <Spinner /> : cases.length === 0 ? (
        <EmptyState title="No cases match" hint="Try clearing the filters, or log a referral in Intake." />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left font-medium px-4 py-3">Case</th>
                <th className="text-left font-medium px-4 py-3">Subject</th>
                <th className="text-left font-medium px-4 py-3">Scheme</th>
                <th className="text-left font-medium px-4 py-3">Stage</th>
                <th className="text-left font-medium px-4 py-3">Priority</th>
                <th className="text-right font-medium px-4 py-3">$ at risk</th>
                <th className="text-left font-medium px-4 py-3">Opened</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} onClick={() => onOpenCase(c.id)} className="border-t border-slate-100 hover:bg-red-50/40 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-slate-400">{c.case_number}</div>
                    <div className="text-slate-700 line-clamp-1 max-w-xs">{c.allegation_summary || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {c.subject_name || '—'}
                    {c.exclusion_flag && <Badge tone="red">Excluded</Badge>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{CATEGORY_LABEL[c.allegation_category] || '—'}</td>
                  <td className="px-4 py-3"><Badge tone={STAGE_TONE[c.stage] || 'slate'}>{STAGE_LABEL[c.stage] || c.stage}</Badge></td>
                  <td className="px-4 py-3"><Badge tone={PRIORITY_TONE[c.priority] || 'slate'}>{c.priority}</Badge></td>
                  <td className="px-4 py-3 text-right text-slate-700">{money(c.estimated_exposure)}</td>
                  <td className="px-4 py-3 text-slate-500">{dateShort(c.opened_at || c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
